import { useState } from "react";
import { Globe, X, Phone, MapPin, Check, PackageCheck, Ban } from "lucide-react";
import { api, fmtGYD, apiErr } from "@/lib/api";

const STATUS = {
  placed: { label: "New", cls: "bg-indigo-500/20 text-indigo-300" },
  confirmed: { label: "Confirmed", cls: "bg-sky-500/20 text-sky-300" },
  ready: { label: "Ready for pickup", cls: "bg-amber-500/20 text-amber-300" },
  fulfilled: { label: "Fulfilled", cls: "bg-emerald-500/20 text-emerald-300" },
  cancelled: { label: "Cancelled", cls: "bg-rose-500/20 text-rose-300" },
};
const NEXT = { placed: [["confirmed", "Confirm", Check], ["cancelled", "Cancel", Ban]], confirmed: [["ready", "Mark ready", PackageCheck], ["cancelled", "Cancel", Ban]], ready: [["fulfilled", "Fulfilled / handed over", Check], ["cancelled", "Cancel", Ban]] };

export function OrderStatusBadge({ status }) {
  const s = STATUS[status] || { label: status, cls: "bg-slate-700 text-slate-300" };
  return <span data-testid="order-status" className={`text-[11px] px-2 py-0.5 rounded font-medium ${s.cls}`}>{s.label}</span>;
}

export function OnlineOrderCard({ o, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const move = async (status) => {
    if (status === "cancelled" && !window.confirm("Cancel this order and return the stock to the shelf?")) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/online-orders/${o.id}/status`, { status }); onChanged(data); }
    catch (e) { setErr(navigator.onLine ? apiErr(e) : "Go online to update orders"); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`online-order-${o.id}`} className="ms-card rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><Globe size={14} className="text-indigo-400" /> {o.quantity} × {o.product_name || o.product_id?.slice(0, 8)} <span className="mono text-indigo-300">{fmtGYD(o.total)}</span></div>
          <div className="text-xs text-slate-400 mt-1">GuyanaDiscounts.com · {new Date(o.created_at).toLocaleString()} · #{o.id.slice(0, 8)}</div>
          <div className="text-sm mt-2">{o.customer_name || "Customer"}{o.customer_phone && <span className="text-slate-400 inline-flex items-center gap-1 ml-2"><Phone size={12} /> {o.customer_phone}</span>}</div>
          {o.delivery_address && <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {o.delivery_address}</div>}
        </div>
        <OrderStatusBadge status={o.status} />
      </div>
      {err && <div className="text-xs text-rose-400 mt-2">{err}</div>}
      {(NEXT[o.status] || []).length > 0 && (
        <div className="flex gap-2 mt-3">
          {NEXT[o.status].map(([st, label, Icon]) => (
            <button key={st} data-testid={`order-${st}-${o.id}`} disabled={busy} onClick={() => move(st)}
              className={`touch-btn text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50 ${st === "cancelled" ? "ms-card ms-card-hover text-rose-300" : "bg-indigo-600 hover:bg-indigo-500"}`}><Icon size={13} /> {label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function OnlineOrdersList({ orders, onChanged, emptyText = "No GuyanaDiscounts.com orders yet." }) {
  const open = orders.filter((o) => !["fulfilled", "cancelled"].includes(o.status));
  const done = orders.filter((o) => ["fulfilled", "cancelled"].includes(o.status));
  return (
    <div className="space-y-3" data-testid="online-orders-list">
      {orders.length === 0 && <div className="text-slate-500 text-sm text-center py-8">{emptyText}</div>}
      {open.map((o) => <OnlineOrderCard key={o.id} o={o} onChanged={onChanged} />)}
      {done.length > 0 && <details className="text-sm text-slate-400"><summary className="cursor-pointer py-1">Completed / cancelled ({done.length})</summary><div className="space-y-2 mt-2">{done.map((o) => <OnlineOrderCard key={o.id} o={o} onChanged={onChanged} />)}</div></details>}
    </div>
  );
}

export function OnlineOrdersModal({ orders, onClose, onChanged }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="ms-card p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="online-orders-modal">
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-lg flex items-center gap-2"><Globe size={18} className="text-indigo-400" /> GuyanaDiscounts.com Orders</h3><button data-testid="online-orders-close" onClick={onClose}><X size={20} /></button></div>
        <p className="text-xs text-slate-400 mb-3">Stock was reserved when each order was placed online — fulfilling does not deduct again; cancelling returns it to the shelf.</p>
        <OnlineOrdersList orders={orders} onChanged={onChanged} />
      </div>
    </div>
  );
}
